import {
  planInputSchema,
  sekToOre,
  oreToSek,
  type AdminPlan,
  type AdminSubscriptionListItem,
  type PlanInput,
  type SubscriptionStatus,
} from '@hemmafixa/shared';
import {
  Badge,
  BarChart,
  Button,
  Card,
  ChartCard,
  Checkbox,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChips,
  FormField,
  InlinePanel,
  InlinePanelFooter,
  Input,
  Label,
  LineChart,
  PageHeader,
  Pagination,
  StatStrip,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  type ColumnDef,
} from '@hemmafixa/ui';
import { AlertTriangle, CreditCard, Hourglass, Pencil, TrendingUp, Users } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { QueryView } from '../shared/query-view';
import { useMonthPoints } from '../shared/stats-parts';
import { useStatuses } from '../shared/statuses';
import { useAdminPlans, useAdminSubscriptions, useRevenue, useSavePlan } from './api';

const PAGE_SIZE = 20;

/** Subscriptions, revenue and the plan editor for admins with the finance permission (P6-08). */
export function AdminBillingPage() {
  const { t } = useI18n();
  const b = t.admin.billing;
  return (
    <div className="grid gap-4">
      <PageHeader title={b.title} description={b.description} />
      <Tabs defaultValue="overview" className="grid gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="overview">{b.tabs.overview}</TabsTrigger>
          <TabsTrigger value="subscriptions">{b.tabs.subscriptions}</TabsTrigger>
          <TabsTrigger value="plans">{b.tabs.plans}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <RevenueOverview />
        </TabsContent>
        <TabsContent value="subscriptions">
          <SubscriptionList />
        </TabsContent>
        <TabsContent value="plans">
          <PlanEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RevenueOverview() {
  const { t, money, language } = useI18n();
  const b = t.admin.billing;
  const query = useRevenue();
  const toPoints = useMonthPoints();
  const percent = new Intl.NumberFormat(language === 'sv' ? 'sv-SE' : 'en-GB', {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const data = query.data;
  const months = data?.months ?? [];
  const thisMonth = months.at(-1);
  const revenue = toPoints(months.map((m) => ({ month: m.month, value: m.revenueOre })));

  return (
    <div className="grid gap-4">
      <StatStrip
        loading={query.isPending}
        items={[
          { label: b.mrr, value: money(data?.mrrOre ?? 0), hint: b.mrrHint, icon: TrendingUp, tone: 'brand' },
          {
            label: b.active,
            value: data?.counts.active ?? 0,
            hint: fill(b.churnHint, {
              rate: thisMonth && thisMonth.churnRate !== null ? percent.format(thisMonth.churnRate) : '–',
            }),
            icon: Users,
            tone: 'success',
          },
          { label: b.trialing, value: data?.counts.trialing ?? 0, icon: Hourglass, tone: 'info' },
          { label: b.pastDue, value: data?.counts.past_due ?? 0, icon: AlertTriangle, tone: 'danger' },
        ]}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={b.revenue} description={b.revenueHint} loading={query.isPending}>
          <BarChart title={b.revenue} data={revenue} format={money} />
        </ChartCard>
        <ChartCard title={b.startedChurned} loading={query.isPending}>
          <LineChart
            title={b.startedChurned}
            labels={revenue}
            series={[
              { name: b.started, color: 1, values: months.map((m) => m.started) },
              { name: b.churned, color: 2, values: months.map((m) => m.churned) },
            ]}
          />
        </ChartCard>
      </div>
    </div>
  );
}

const STATUS_FILTERS: SubscriptionStatus[] = ['trialing', 'active', 'past_due', 'canceled', 'incomplete'];

function SubscriptionList() {
  const { t, money, date, language } = useI18n();
  const b = t.admin.billing;
  const statuses = useStatuses();
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus | 'all'>('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useAdminSubscriptions({
    q: searchQuery || undefined,
    status: status === 'all' ? undefined : status,
    page,
    pageSize: PAGE_SIZE,
  });

  const nextDate = (row: AdminSubscriptionListItem) => {
    if (row.status === 'past_due' && row.graceEndsAt)
      return fill(b.graceUntil, { date: date(row.graceEndsAt) });
    if (row.status === 'trialing' && row.trialEndsAt)
      return fill(b.trialUntil, { date: date(row.trialEndsAt) });
    if (row.cancelAtPeriodEnd && row.currentPeriodEnd)
      return fill(b.endsOn, { date: date(row.currentPeriodEnd) });
    return row.currentPeriodEnd ? date(row.currentPeriodEnd) : '–';
  };

  const columns = useMemo<ColumnDef<AdminSubscriptionListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'companyName',
        header: b.columns.company,
        cell: ({ row }) => (
          <div className="grid">
            <span className="font-medium">{row.original.companyName ?? b.noCompany}</span>
            <span className="text-xs text-muted-foreground">{row.original.email}</span>
          </div>
        ),
      },
      {
        accessorKey: 'planNameSv',
        header: b.columns.plan,
        cell: ({ row }) => (language === 'sv' ? row.original.planNameSv : row.original.planNameEn),
      },
      { accessorKey: 'priceOre', header: b.columns.price, cell: ({ row }) => money(row.original.priceOre) },
      {
        accessorKey: 'status',
        header: b.columns.status,
        cell: ({ row }) => <StatusBadge status={row.original.status} statuses={statuses.subscription} />,
      },
      {
        id: 'next',
        header: b.columns.renews,
        enableSorting: false,
        cell: ({ row }) => nextDate(row.original),
      },
      {
        accessorKey: 'createdAt',
        header: b.columns.started,
        cell: ({ row }) => date(row.original.createdAt),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nextDate only reads b and date
    [b, money, date, language, statuses],
  );

  const filtered = search !== '' || status !== 'all';
  return (
    <div className="grid gap-4">
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder={b.searchPlaceholder}
        summary={
          query.data
            ? fill(t.common.showingCount, { shown: query.data.items.length, total: query.data.total })
            : undefined
        }
        hasActiveFilters={filtered}
        onClear={() => {
          setSearch('');
          setStatus('all');
          setPage(1);
        }}
        clearLabel={t.common.clearFilters}
        chips={
          <FilterChips
            label={b.columns.status}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={[
              { value: 'all' as const, label: b.allStatuses },
              ...STATUS_FILTERS.map((s) => ({ value: s, label: t.status.subscription[s] })),
            ]}
          />
        }
      />
      <QueryView query={query}>
        {(result) => (
          <>
            <DataTable
              columns={columns}
              data={result.items}
              getRowId={(row) => row.id}
              empty={
                filtered ? (
                  <EmptyState
                    icon={CreditCard}
                    title={t.common.noMatchesTitle}
                    description={t.common.noMatchesText}
                  />
                ) : (
                  <EmptyState icon={CreditCard} title={b.empty} />
                )
              }
            />
            <Pagination
              page={result.page}
              pageSize={result.pageSize}
              total={result.total}
              onPageChange={setPage}
              labels={{
                previous: t.common.previous,
                next: t.common.next,
                showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
              }}
            />
          </>
        )}
      </QueryView>
    </div>
  );
}

function PlanEditor() {
  const query = useAdminPlans();
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <QueryView query={query} loading="cards">
      {(plans) => (
        <div className="grid gap-4 lg:grid-cols-3">
          {plans.map((plan) =>
            editing === plan.id ? (
              <PlanForm key={plan.id} plan={plan} onDone={() => setEditing(null)} />
            ) : (
              <PlanCard key={plan.id} plan={plan} onEdit={() => setEditing(plan.id)} />
            ),
          )}
        </div>
      )}
    </QueryView>
  );
}

function PlanCard({ plan, onEdit }: { plan: AdminPlan; onEdit: () => void }) {
  const { t, money, language } = useI18n();
  const b = t.admin.billing;
  return (
    <Card className="grid content-start gap-3 p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">{language === 'sv' ? plan.nameSv : plan.nameEn}</h2>
          <p className="text-sm text-muted-foreground">{fill(b.subscribers, { count: plan.subscribers })}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil aria-hidden />
          {b.edit}
        </Button>
      </div>
      <p className="text-2xl font-bold tabular-nums">{money(plan.priceOre)}</p>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-muted-foreground">{b.fields.proposals}</dt>
        <dd className="text-right tabular-nums">{plan.proposalsPerMonth}</dd>
        <dt className="text-muted-foreground">{b.fields.trialDays}</dt>
        <dd className="text-right tabular-nums">{plan.trialDays}</dd>
      </dl>
      <div className="flex flex-wrap gap-2">
        {!plan.active && <Badge tone="warning">{b.inactive}</Badge>}
        {!plan.inStripe && <Badge>{b.notInStripe}</Badge>}
      </div>
    </Card>
  );
}

type PlanValues = Record<
  'nameSv' | 'nameEn' | 'descriptionSv' | 'descriptionEn' | 'price' | 'proposals' | 'trialDays' | 'position',
  string
> & { active: boolean };

const toValues = (plan: AdminPlan): PlanValues => ({
  nameSv: plan.nameSv,
  nameEn: plan.nameEn,
  descriptionSv: plan.descriptionSv ?? '',
  descriptionEn: plan.descriptionEn ?? '',
  price: String(oreToSek(plan.priceOre)),
  proposals: String(plan.proposalsPerMonth),
  trialDays: String(plan.trialDays),
  position: String(plan.position),
  active: plan.active,
});

function toInput(values: PlanValues) {
  const number = (value: string) => Number(value.replace(',', '.').replace(/\s/g, ''));
  return planInputSchema.safeParse({
    nameSv: values.nameSv,
    nameEn: values.nameEn,
    descriptionSv: values.descriptionSv.trim() || null,
    descriptionEn: values.descriptionEn.trim() || null,
    priceOre: sekToOre(number(values.price)),
    proposalsPerMonth: number(values.proposals),
    trialDays: number(values.trialDays),
    position: number(values.position),
    active: values.active,
  } satisfies Record<keyof PlanInput, unknown>);
}

function PlanForm({ plan, onDone }: { plan: AdminPlan; onDone: () => void }) {
  const { t } = useI18n();
  const b = t.admin.billing;
  const f = b.fields;
  const save = useSavePlan(plan.id);
  const [values, setValues] = useState(() => toValues(plan));
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const set = (key: keyof PlanValues) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = toInput(values);
    if (!parsed.success) {
      setInvalid(new Set(parsed.error.issues.map((issue) => String(issue.path[0]))));
      toast.error(b.invalid);
      return;
    }
    save.mutate(parsed.data, {
      onSuccess: () => {
        toast.success(b.saved);
        onDone();
      },
      onError: (error) => toast.error(error instanceof ApiError ? error.message : t.auth.genericError),
    });
  };
  const error = (key: keyof PlanInput) => (invalid.has(key) ? b.invalid : undefined);
  const text = (
    key: keyof PlanValues,
    label: string,
    field: keyof PlanInput,
    inputMode?: 'numeric' | 'decimal',
  ) => (
    <FormField label={label} error={error(field)} required>
      <Input
        inputMode={inputMode}
        value={values[key] as string}
        onChange={(event) => set(key)(event.target.value)}
      />
    </FormField>
  );

  return (
    <InlinePanel
      title={fill(b.editTitle, { name: plan.nameSv })}
      description={b.priceNote}
      onClose={onDone}
      onSubmit={submit}
      closeLabel={t.common.cancel}
      className="lg:col-span-3"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {text('nameSv', f.nameSv, 'nameSv')}
        {text('nameEn', f.nameEn, 'nameEn')}
        <FormField label={f.descriptionSv} error={error('descriptionSv')}>
          <Textarea
            value={values.descriptionSv}
            onChange={(event) => set('descriptionSv')(event.target.value)}
          />
        </FormField>
        <FormField label={f.descriptionEn} error={error('descriptionEn')}>
          <Textarea
            value={values.descriptionEn}
            onChange={(event) => set('descriptionEn')(event.target.value)}
          />
        </FormField>
        {text('price', f.price, 'priceOre', 'decimal')}
        {text('proposals', f.proposals, 'proposalsPerMonth', 'numeric')}
        {text('trialDays', f.trialDays, 'trialDays', 'numeric')}
        {text('position', f.position, 'position', 'numeric')}
        <div className="flex items-center gap-2 sm:col-span-2">
          <Checkbox
            id={`plan-active-${plan.id}`}
            checked={values.active}
            onCheckedChange={(checked) => setValues((current) => ({ ...current, active: checked === true }))}
          />
          <Label htmlFor={`plan-active-${plan.id}`}>{f.active}</Label>
        </div>
      </div>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          {t.common.cancel}
        </Button>
        <Button type="submit" loading={save.isPending}>
          {t.common.save}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
